import {
  createDataBindingKeyManagerError,
  DataBindingKeyManagerErrorCode,
} from "./errors";

/**
 * Binding key for data item bindings
 *
 * Storage format: type::innerElementPath::id::subItemId
 * - type: REQUIRED ('dataItem', future: 'styleItem', etc.)
 * - innerElementPath: Extracted from compRef parameter
 * - id: REQUIRED
 * - subItemId: optional (omit to match all sub-items)
 *
 * Note: Connection-level type 'binding' identifies these as binding connections
 */
export interface BindingKey {
  /** Binding type (required) - currently only 'dataItem' supported */
  type: "dataItem";

  /** Data item identifier (required) */
  id: string;

  /**
   * Sub-item identifier for partial bindings (optional)
   * Example: 'label', 'src', 'textColor'
   * Omit to match all sub-items
   */
  subItemId?: string;
}

/**
 * Decoded binding key with path information
 */
export interface DecodedBindingKey {
  type: "dataItem";
  id: string;
  innerElementPath: string[];
  subItemId?: string;
}

/**
 * Manages encoding/decoding of binding keys
 */
export class BindingKeyManager {
  private static readonly DELIMITER = "::";
  private static readonly PATH_SEPARATOR = "/";

  /**
   * Encodes binding key to storage string
   *
   * Format: type::innerElementPath::id::subItemId
   *
   * Examples:
   *   ([], { type: 'dataItem', id: 'item1' })
   *     → "dataItem::::item1::"
   *
   *   (['container', 'button'], { type: 'dataItem', id: 'item1' })
   *     → "dataItem::container/button::item1::"
   *
   *   ([], { type: 'dataItem', id: 'item1', subItemId: 'label' })
   *     → "dataItem::::item1::label"
   */
  static encode(innerElementPath: string[], key: BindingKey): string {
    const pathPart = innerElementPath.length
      ? innerElementPath.join(this.PATH_SEPARATOR)
      : "";

    const subItemPart = key.subItemId || "";

    return `${key.type}${this.DELIMITER}${pathPart}${this.DELIMITER}${key.id}${this.DELIMITER}${subItemPart}`;
  }

  /**
   * Decodes storage string to binding key with path
   * Supports legacy format:
   * - Plain id: "item1" → { type: 'dataItem', id: 'item1', innerElementPath: [] }
   * New format:
   * - 4 parts (with type): "dataItem::::item1::" → { type: 'dataItem', id: 'item1', innerElementPath: [] }
   */
  static decode(encoded: string): DecodedBindingKey {
    // Legacy format: no delimiters (plain id from origin)
    if (!encoded.includes(this.DELIMITER)) {
      return {
        type: "dataItem",
        id: encoded,
        innerElementPath: [],
      };
    }

    const parts = encoded.split(this.DELIMITER);

    // New format: type::pathPart::id::subItemPart (4 parts)
    if (parts.length === 4) {
      const [type, pathPart, id, subItemPart] = parts;

      if (type !== "dataItem") {
        throw createDataBindingKeyManagerError(
          DataBindingKeyManagerErrorCode.UnsupportedBindingType,
          `Unsupported binding type: ${type}`
        );
      }

      return {
        type: "dataItem",
        id,
        innerElementPath: pathPart ? pathPart.split(this.PATH_SEPARATOR) : [],
        subItemId: subItemPart || undefined,
      };
    }

    throw createDataBindingKeyManagerError(
      DataBindingKeyManagerErrorCode.InvalidBindingKeyFormat,
      `Invalid binding key format: ${encoded}`
    );
  }
}
